import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, ArrowUpRight, MessageCircle, Loader2, Check } from 'lucide-react';
import Reveal from '@/components/Reveal';
import Seo from '@/components/Seo';
import Faq from '@/components/Faq';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { APPOINTMENT_PAGE } from '@/constants/testIds';
import { CONTACT, buildWhatsappLink, WHATSAPP } from '@/constants/site';
import { APPOINTMENT_FAQS } from '@/constants/faqs';
import { submitAppointment } from '@/lib/api';

const TIMES = [
  '10:00', '11:00', '12:00', '13:00',
  '14:00', '15:00', '16:00', '17:00',
  '18:00', '19:00', '20:00',
];

export default function BookAppointment() {
  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    email: '',
    service_required: '',
    preferred_date: null,
    preferred_time: '',
    occasion: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.full_name.trim()) return 'Please share your full name.';
    if (!form.phone.trim()) return 'A contact number is required.';
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return 'A valid email is required.';
    if (!form.service_required) return 'Please choose a service.';
    if (!form.preferred_date) return 'Please select a preferred date.';
    if (!form.preferred_time) return 'Please select a preferred time.';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(form.preferred_date, 'yyyy-MM-dd'),
      };
      const res = await submitAppointment(payload);
      toast.success('Appointment request received. Opening WhatsApp to confirm…');
      setSuccess(res);
      // Auto-open WhatsApp with the prefilled message so the owner is notified directly
      if (res?.whatsapp_link) {
        window.open(res.whatsapp_link, '_blank', 'noopener,noreferrer');
      }
    } catch (e2) {
      const msg = e2?.response?.data?.detail || 'Something went wrong. Please try again or WhatsApp us directly.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-[hsl(var(--bb-ivory))]" data-testid={APPOINTMENT_PAGE.success}>
        <div className="bb-container py-24 md:py-36 text-center max-w-2xl">
          <Reveal>
            <div className="mx-auto w-16 h-16 border border-[hsl(var(--bb-gold))]/50 flex items-center justify-center">
              <Check size={28} className="text-[hsl(var(--bb-gold-deep))]" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="bb-display text-5xl md:text-6xl mt-8 text-[hsl(var(--bb-text))]">
              Thank you, <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">{form.full_name.split(' ')[0]}</span>.
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-6 text-[hsl(var(--bb-text-muted))] text-base md:text-lg leading-relaxed">
              Your request has been received. Our atelier will be in touch shortly to
              confirm your appointment. A WhatsApp draft has opened in a new tab — please
              tap send to notify us instantly.
            </p>
          </Reveal>
          <Reveal delay={320}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href={success.whatsapp_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.22em] text-[11px] font-medium hover:bg-[hsl(var(--bb-gold-deep))] transition-colors"
              >
                <MessageCircle size={16} /> Re-open WhatsApp draft
              </a>
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                className="bb-link bb-link-reverse text-[hsl(var(--bb-text))]"
              >
                Or call {CONTACT.phone} →
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={APPOINTMENT_PAGE.root} className="bg-[hsl(var(--bb-ivory))]">
      <Seo
        title="Book an Appointment — Bombay Bespoke Fashion House | Dubai"
        description="Reserve a private consultation at Bombay Bespoke Fashion House, Al Karama, Dubai. Bespoke menswear and women's couture by appointment — pick a date and we'll prepare the cloth."
        canonical="https://bombaybespokefashion.com/book-appointment"
      />
      <section className="bb-container pt-14 md:pt-24 pb-12">
        <Reveal>
          <div className="bb-eyebrow">Book an Appointment</div>
        </Reveal>
        <div className="grid grid-cols-12 gap-10 mt-4">
          <div className="col-span-12 lg:col-span-7">
            <Reveal delay={100}>
              <h1 className="bb-display text-5xl md:text-7xl text-[hsl(var(--bb-text))]">
                A garment begins
                <br />
                <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">with a conversation.</span>
              </h1>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <Reveal delay={180}>
              <p className="text-[hsl(var(--bb-text-muted))] text-base md:text-lg leading-relaxed">
                Tell us a little about the occasion and we will prepare the cloth, the
                references, and the time. Consultations are private and unhurried.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bb-container pb-24 md:pb-32">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          <form
            onSubmit={onSubmit}
            data-testid={APPOINTMENT_PAGE.form}
            className="col-span-12 lg:col-span-8 border border-[hsl(var(--bb-border))] bg-[hsl(var(--bb-ivory))]"
          >
            <div className="p-8 md:p-12 grid grid-cols-12 gap-x-8 gap-y-7">
              <Field label="Full Name" cols="col-span-12 md:col-span-6">
                <Input
                  data-testid={APPOINTMENT_PAGE.inputName}
                  value={form.full_name}
                  onChange={(e) => update('full_name', e.target.value)}
                  className="bb-input"
                  placeholder="Your full name"
                />
              </Field>
              <Field label="Phone Number" cols="col-span-12 md:col-span-6">
                <Input
                  data-testid={APPOINTMENT_PAGE.inputPhone}
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  className="bb-input"
                  placeholder="+971 …"
                />
              </Field>
              <Field label="Email Address" cols="col-span-12">
                <Input
                  data-testid={APPOINTMENT_PAGE.inputEmail}
                  type="email"
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                  className="bb-input"
                  placeholder="you@email.com"
                />
              </Field>

              <Field label="Service Required" cols="col-span-12">
                <Select
                  value={form.service_required}
                  onValueChange={(v) => update('service_required', v)}
                >
                  <SelectTrigger data-testid={APPOINTMENT_PAGE.service} className="bb-select-trigger">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="womens" data-testid={APPOINTMENT_PAGE.serviceOption('womens')}>Women’s Design — VM by Vandana</SelectItem>
                    <SelectItem value="mens" data-testid={APPOINTMENT_PAGE.serviceOption('mens')}>Men’s Bespoke — Bombay Bespoke</SelectItem>
                    <SelectItem value="both" data-testid={APPOINTMENT_PAGE.serviceOption('both')}>Both</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Preferred Date" cols="col-span-12 md:col-span-6">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid={APPOINTMENT_PAGE.datePickerBtn}
                      className="bb-input flex items-center justify-between text-left w-full"
                    >
                      <span className={form.preferred_date ? 'text-[hsl(var(--bb-text))]' : 'text-[hsl(var(--bb-text-muted))]'}>
                        {form.preferred_date ? format(form.preferred_date, 'd MMMM yyyy') : 'Select a date'}
                      </span>
                      <CalendarIcon size={16} className="text-[hsl(var(--bb-gold-deep))]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      data-testid={APPOINTMENT_PAGE.calendar}
                      selected={form.preferred_date}
                      onSelect={(d) => update('preferred_date', d)}
                      disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </Field>

              <Field label="Preferred Time" cols="col-span-12 md:col-span-6">
                <Select
                  value={form.preferred_time}
                  onValueChange={(v) => update('preferred_time', v)}
                >
                  <SelectTrigger data-testid={APPOINTMENT_PAGE.inputTime} className="bb-select-trigger">
                    <SelectValue placeholder="Select a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Occasion (optional)" cols="col-span-12">
                <Input
                  data-testid={APPOINTMENT_PAGE.inputOccasion}
                  value={form.occasion}
                  onChange={(e) => update('occasion', e.target.value)}
                  className="bb-input"
                  placeholder="Wedding · Reception · Black tie · Personal wardrobe"
                />
              </Field>

              <Field label="Message" cols="col-span-12">
                <Textarea
                  data-testid={APPOINTMENT_PAGE.inputMessage}
                  rows={4}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  className="bb-textarea"
                  placeholder="A few details about what you have in mind…"
                />
              </Field>
            </div>

            <div className="p-8 md:p-10 border-t border-[hsl(var(--bb-border))] flex flex-wrap items-center justify-between gap-4 bg-[hsl(var(--bb-ivory-soft))]">
              <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--bb-text-muted))]">
                On submit, we’ll open a WhatsApp draft to {CONTACT.phone}.
              </p>
              <button
                type="submit"
                data-testid={APPOINTMENT_PAGE.submit}
                disabled={loading}
                className="inline-flex items-center gap-3 px-8 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-medium hover:bg-[hsl(var(--bb-gold-deep))] transition-colors duration-500 disabled:opacity-60"
              >
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Submit request <ArrowUpRight size={16} /></>}
              </button>
            </div>
          </form>

          <aside className="col-span-12 lg:col-span-4">
            <Reveal className="border border-[hsl(var(--bb-border))] p-7 md:p-8">
              <div className="bb-eyebrow">Prefer WhatsApp?</div>
              <h3 className="bb-display text-3xl md:text-4xl mt-3 text-[hsl(var(--bb-text))]">
                Speak with us <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">directly.</span>
              </h3>
              <p className="mt-4 text-sm text-[hsl(var(--bb-text-muted))] leading-relaxed">
                Send us a message on WhatsApp and we’ll reply within business hours.
              </p>
              <a
                href={buildWhatsappLink(WHATSAPP.defaultMessage)}
                target="_blank"
                rel="noreferrer"
                data-testid={APPOINTMENT_PAGE.whatsappFallback}
                onClick={() => window.gtag && window.gtag('event', 'Whatsapp', { source: 'appointment-page' })}
                className="mt-6 inline-flex items-center gap-3 px-6 py-3.5 bg-[hsl(var(--bb-emerald))] text-[hsl(var(--bb-text))] uppercase tracking-[0.22em] text-[11px] font-medium hover:bg-[hsl(var(--bb-emerald-deep))] transition-colors duration-500"
              >
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>

              <div className="my-7 h-px bg-[hsl(var(--bb-border))]" />
              <div className="bb-eyebrow">Visit</div>
              <div className="mt-3 text-sm text-[hsl(var(--bb-text-muted))] leading-relaxed">
                {CONTACT.addressLine1}<br />
                {CONTACT.addressLine2}<br />
                <span className="block mt-3 text-[hsl(var(--bb-text))]">{CONTACT.hours}</span>
                <span>{CONTACT.hoursSunday}</span>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="bg-[hsl(var(--bb-ivory-soft))] border-t border-[hsl(var(--bb-text))]/8">
        <Faq
          eyebrow="Booking · FAQs"
          heading="Before you"
          italicWord="arrive."
          items={APPOINTMENT_FAQS}
          tone="gold"
        />
      </section>

      {/* form input styles via inline style tag — kept here to avoid global pollution */}
      <style>{`
        .bb-input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid hsl(var(--bb-text) / 0.22);
          border-radius: 0;
          padding: 14px 0 12px;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          color: hsl(var(--bb-text));
          outline: none;
          transition: border-color 0.4s ease;
        }
        .bb-input::placeholder { color: hsl(var(--bb-text-muted)); opacity: 0.7; }
        .bb-input:focus { border-bottom-color: hsl(var(--bb-gold)); }
        .bb-textarea { resize: vertical; min-height: 100px; }
        .bb-select-trigger {
          width: 100%;
          background: transparent !important;
          border: none !important;
          border-bottom: 1px solid hsl(var(--bb-text) / 0.22) !important;
          border-radius: 0 !important;
          padding: 14px 0 12px !important;
          height: auto !important;
          color: hsl(var(--bb-text));
        }
        .bb-select-trigger:focus { border-bottom-color: hsl(var(--bb-gold)) !important; box-shadow: none !important; }
      `}</style>
    </div>
  );
}

function Field({ label, cols, children }) {
  return (
    <div className={cols}>
      <Label className="block text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--bb-text-muted))] mb-2">
        {label}
      </Label>
      {children}
    </div>
  );
}
